import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import { useTelegram } from "@/contexts/UnifiedAuthContext";
import { 
  getThemePreference, 
  saveThemePreference, 
  detectSystemTheme, 
  createSystemThemeListener, 
  applyPresetTheme 
} from "@/services/themeService";

export type ThemeMode = 'light' | 'dark' | 'auto';
export type ResolvedTheme = 'light' | 'dark';

interface ThemeContextType {
  themeMode: ThemeMode;
  resolvedTheme: ResolvedTheme;
  setThemeMode: (mode: ThemeMode) => void;
  isSystemDarkMode: boolean;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (context === undefined) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};

interface ThemeProviderProps {
  children: React.ReactNode;
}

export const ThemeProvider: React.FC<ThemeProviderProps> = ({ children }) => {
  const { isTelegram, colorScheme: telegramColorScheme } = useTelegram();
  
  // Состояние для пользовательского выбора темы
  const [themeMode, setThemeModeState] = useState<ThemeMode>('auto');
  
  // Состояние для системной темы
  const [isSystemDarkMode, setIsSystemDarkMode] = useState<boolean>(false);
  
  // Вычисляемая финальная тема
  const resolvedTheme: ResolvedTheme = React.useMemo(() => {
    // Приоритет 1: Telegram режим - всегда используем тему из Telegram
    if (isTelegram && telegramColorScheme) {
      return telegramColorScheme;
    }
    
    // Приоритет 2-4: Пользовательские настройки для браузера
    switch (themeMode) {
      case 'light':
        return 'light';
      case 'dark':
        return 'dark';
      case 'auto':
        return isSystemDarkMode ? 'dark' : 'light';
      default:
        return 'light';
    }
  }, [isTelegram, telegramColorScheme, themeMode, isSystemDarkMode]);

  // Функция для установки режима темы с сохранением в localStorage
  const setThemeMode = useCallback((mode: ThemeMode) => {
    setThemeModeState(mode);
    saveThemePreference(mode);
  }, []);

  // Функция применения CSS класса и CSS переменных темы
  const applyThemeClass = useCallback((theme: ResolvedTheme) => {
    const root = document.documentElement;
    
    // Убираем все классы тем
    root.classList.remove('light', 'dark', 'telegram');
    
    // В Telegram режиме добавляем специальный класс
    if (isTelegram) {
      root.classList.add('telegram', theme);
      // В Telegram темы применяются через telegramService.applyTelegramTheme
    } else {
      // В браузере применяем наши CSS переменные
      root.classList.add(theme);
      applyPresetTheme(theme);
    }
  }, [isTelegram]);

  // Инициализация темы при загрузке
  useEffect(() => {
    // Загружаем сохраненную настройку темы (только для браузера, не для Telegram)
    if (!isTelegram) {
      const savedTheme = getThemePreference();
      setThemeModeState(savedTheme);
    }

    // Определяем системную тему
    const systemDark = detectSystemTheme();
    setIsSystemDarkMode(systemDark);

    // Подписываемся на изменения системной темы
    const unsubscribe = createSystemThemeListener((isDark) => {
      setIsSystemDarkMode(isDark);
    });

    return unsubscribe;
  }, [isTelegram]);

  // Применяем тему при изменении resolvedTheme
  useEffect(() => {
    applyThemeClass(resolvedTheme);
  }, [resolvedTheme, applyThemeClass]);

  const value: ThemeContextType = {
    themeMode,
    resolvedTheme,
    setThemeMode,
    isSystemDarkMode,
  };

  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  );
};